import { getMutes, type PostView, setMute } from "../queries.ts";
import { Block } from "../structs.ts";
import { RpcClient } from "./api.ts";
import { authenticatedAppViewRequest, type AuthenticatedState, type AuthState } from "./auth.ts";
import { ownRecords, setRelation } from "./channels.ts";
import { config } from "./config.ts";
import { canCallRpc, canWriteCollection } from "./permissions.ts";

/**
 * Everything the viewer has asked not to see: per-browser hidden posts and threads, plus a cached
 * view of their AppView mutes and repository blocks. Lists consult `isFiltered` when rendering and
 * cards drop themselves on `filterChanges`.
 */
export const filterChanges = new EventTarget();

export type HiddenKind = "post" | "thread";

const storageKey = "cerulea.hidden";
const actors = { muted: new Set<string>(), blocked: new Set<string>() };
let loadedFor: string | undefined;

function readHidden(): Record<HiddenKind, Set<string>> {
  try {
    const stored = JSON.parse(localStorage.getItem(storageKey) ?? "{}") as Partial<
      Record<HiddenKind, unknown>
    >;
    const list = (value: unknown) =>
      new Set(Array.isArray(value) ? value.filter((v) => typeof v === "string") : []);
    return { post: list(stored.post), thread: list(stored.thread) };
  } catch {
    return { post: new Set(), thread: new Set() };
  }
}

export function threadRoot(post: PostView): string {
  return post.record.reply?.root.uri ?? post.uri;
}

export function hiddenState(post: PostView): HiddenKind | undefined {
  const hidden = readHidden();
  if (hidden.post.has(post.uri)) return "post";
  if (hidden.thread.has(threadRoot(post))) return "thread";
  return undefined;
}

export function setHidden(kind: HiddenKind, uri: string, hidden: boolean): void {
  const current = readHidden();
  current[kind][hidden ? "add" : "delete"](uri);
  localStorage.setItem(
    storageKey,
    JSON.stringify({ post: [...current.post], thread: [...current.thread] }),
  );
  filterChanges.dispatchEvent(new Event("change"));
}

/** Mutes hide posts on feeds and discovery surfaces, but never on the author's own page. */
export function isFiltered(post: PostView, showMuted = false): boolean {
  return hiddenState(post) !== undefined || actors.blocked.has(post.author.did) ||
    (!showMuted && actors.muted.has(post.author.did));
}

export function actorState(did: string): { muted: boolean; blocked: boolean } {
  return { muted: actors.muted.has(did), blocked: actors.blocked.has(did) };
}

export function canModerate(state: AuthenticatedState): boolean {
  return canWriteCollection(state.scopes, Block.id, "create") &&
    canWriteCollection(state.scopes, Block.id, "delete") &&
    [setMute.id, getMutes.id].every((method) =>
      canCallRpc(state.scopes, method, config.appViewService)
    );
}

/** Populate the mute/block cache once per signed-in account; clears it when signed out. */
export async function loadActorFilters(state: AuthState, signal?: AbortSignal): Promise<void> {
  if (state.status !== "authenticated") {
    actors.muted = new Set();
    actors.blocked = new Set();
    loadedFor = undefined;
    return;
  }
  if (loadedFor === state.did || !canModerate(state)) return;
  const viewer = state.did as `did:${string}`;
  const client = new RpcClient(authenticatedAppViewRequest);
  const muted = new Set<string>();
  let cursor: string | undefined;
  do {
    const page = await client.call(getMutes, {
      parameters: { limit: 100, ...(cursor && { cursor }) },
      ...(signal && { signal }),
    });
    for (const actor of page.actors) muted.add(actor.did);
    cursor = page.cursor;
  } while (cursor && muted.size < 5000);
  const blocked = new Set(
    (await ownRecords(viewer, Block.id, signal)).flatMap((entry) =>
      entry.value.$type === Block.id ? [entry.value.subject] : []
    ),
  );
  actors.muted = muted;
  actors.blocked = blocked;
  loadedFor = state.did;
  filterChanges.dispatchEvent(new Event("change"));
}

export async function setMuted(did: `did:${string}`, muted: boolean): Promise<void> {
  await new RpcClient(authenticatedAppViewRequest).call(setMute, {
    parameters: {},
    input: { actor: did, muted },
  });
  actors.muted[muted ? "add" : "delete"](did);
  filterChanges.dispatchEvent(new Event("change"));
}

export async function setBlocked(
  viewer: `did:${string}`,
  did: `did:${string}`,
  blocked: boolean,
): Promise<void> {
  await setRelation(viewer, Block.id, did, blocked);
  actors.blocked[blocked ? "add" : "delete"](did);
  filterChanges.dispatchEvent(new Event("change"));
}
